// src/components/sliders/UploadSliderModal.js
import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { FaCloudUploadAlt, FaTimes } from "react-icons/fa";
import { collection, doc, setDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../firebase";
import {
  validateSliderFile,
  optimizeSliderImage,
  formatBytes,
  ALLOWED_EXTENSIONS
} from "../../utils/imageOptimizer";

const UploadSliderModal = ({ show, onHide, onSuccess, existingSliders = [] }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [sortOrder, setSortOrder] = useState(1);
  const [isActive, setIsActive] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // Compute sensible default sortOrder whenever modal opens or existingSliders change
  useEffect(() => {
    if (show) {
      if (existingSliders.length > 0) {
        const maxOrder = Math.max(...existingSliders.map((s) => Number(s.sortOrder) || 0));
        setSortOrder(maxOrder + 1);
      } else {
        setSortOrder(1);
      }
      setIsActive(true);
      setErrorMsg("");
      setSelectedFile(null);
      setPreviewUrl(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [show, existingSliders]);

  // Clean up object URLs to prevent memory leaks
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleFileChange = (file) => {
    setErrorMsg("");
    if (!file) return;

    try {
      validateSliderFile(file);
      setSelectedFile(file);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      const newUrl = URL.createObjectURL(file);
      setPreviewUrl(newUrl);
    } catch (err) {
      setSelectedFile(null);
      setPreviewUrl(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setErrorMsg(err.message);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileChange(files[0]);
    }
  };

  const clearSelectedFile = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedFile(null);
    setPreviewUrl(null);
    setErrorMsg("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      setErrorMsg("Please select an image banner to upload.");
      return;
    }

    const orderNum = parseInt(sortOrder, 10);
    if (isNaN(orderNum) || orderNum < 0) {
      setErrorMsg("Sort order must be a non-negative number.");
      return;
    }

    try {
      setUploading(true);
      setErrorMsg("");

      // 1. Optimize image client-side (max 1920px width, aspect-ratio preserved, WebP preferred)
      const { blob, contentType, extension } = await optimizeSliderImage(selectedFile);

      // 2. Prepare Firestore document ID and predictable Storage path
      const sliderCollectionRef = collection(db, "sliders");
      const newSliderDocRef = doc(sliderCollectionRef);
      const sliderId = newSliderDocRef.id;
      const storagePath = `sliders/${sliderId}/banner.${extension}`;

      // 3. Upload to Firebase Storage
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, blob, {
        contentType,
        customMetadata: {
          originalName: selectedFile.name,
          uploadedAt: new Date().toISOString(),
        }
      });

      // 4. Retrieve Download URL
      const imageUrl = await getDownloadURL(storageRef);

      // 5. Store metadata in Firestore matching exact schema
      await setDoc(newSliderDocRef, {
        imageUrl,
        storagePath,
        sortOrder: orderNum,
        isActive: Boolean(isActive),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // 6. Reset form & notify parent
      clearSelectedFile();
      if (onSuccess) {
        onSuccess("Slider uploaded successfully!");
      }
      onHide();
    } catch (err) {
      console.error("Error uploading slider:", err);
      setErrorMsg(err.message || "Failed to upload slider. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={() => {
        if (!uploading) {
          clearSelectedFile();
          onHide();
        }
      }}
      centered
      backdrop={uploading ? "static" : true}
      size="lg"
    >
      <Modal.Header closeButton={!uploading} className="border-0 pb-0">
        <Modal.Title className="text-h2" style={{ color: "var(--accent-lime)" }}>
          Upload New Slider
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body className="pt-3">
          {errorMsg && (
            <div className="alert alert-danger py-2 px-3 rounded-3 small mb-3" role="alert">
              {errorMsg}
            </div>
          )}

          {/* Image File Selector & Preview */}
          <div className="mb-4">
            <Form.Label className="fw-semibold theme-form-label mb-2">
              Banner Image <span className="text-danger">*</span>
            </Form.Label>

            {!previewUrl ? (
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => !uploading && fileInputRef.current?.click()}
                style={{
                  border: `2px dashed ${isDragOver ? "var(--accent-lime)" : "var(--divider)"}`,
                  background: isDragOver ? "rgba(217, 255, 31, 0.05)" : "var(--bg-primary)",
                  borderRadius: 12,
                  padding: "36px 20px",
                  textAlign: "center",
                  cursor: uploading ? "not-allowed" : "pointer",
                  transition: "all 0.2s ease",
                }}
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  accept={ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(",")}
                  onChange={(e) => handleFileChange(e.target.files?.[0])}
                  disabled={uploading}
                />
                <FaCloudUploadAlt
                  size={42}
                  style={{ color: isDragOver ? "var(--accent-lime)" : "var(--text-secondary)", marginBottom: 10 }}
                />
                <h6 style={{ color: "var(--text-primary)", fontWeight: 600, marginBottom: 4 }}>
                  Choose a banner image or drag & drop here
                </h6>
                <p style={{ color: "var(--text-secondary)", fontSize: 12, margin: 0 }}>
                  Supports JPG, PNG, WebP (Max 5 MB). Will be automatically optimized up to 1920px.
                </p>
              </div>
            ) : (
              <div
                style={{
                  background: "var(--bg-primary)",
                  border: "1px solid var(--divider)",
                  borderRadius: 12,
                  padding: 14,
                  position: "relative",
                }}
              >
                {/* Remove button */}
                <button
                  type="button"
                  onClick={clearSelectedFile}
                  disabled={uploading}
                  style={{
                    position: "absolute",
                    top: 20,
                    right: 20,
                    background: "rgba(0, 0, 0, 0.75)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    borderRadius: "50%",
                    width: 32,
                    height: 32,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    cursor: uploading ? "not-allowed" : "pointer",
                    zIndex: 2,
                  }}
                  title="Remove Image"
                >
                  <FaTimes size={14} />
                </button>

                {/* Banner preview container with 16:9 / wide banner aspect */}
                <div
                  style={{
                    width: "100%",
                    maxHeight: 240,
                    borderRadius: 8,
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#000",
                  }}
                >
                  <img
                    src={previewUrl}
                    alt="Slider Preview"
                    style={{
                      maxWidth: "100%",
                      maxHeight: 240,
                      objectFit: "contain",
                      display: "block",
                    }}
                  />
                </div>

                <div className="d-flex align-items-center justify-content-between mt-2 px-1">
                  <span className="small text-truncate" style={{ color: "var(--text-primary)", maxWidth: "70%" }}>
                    {selectedFile?.name}
                  </span>
                  <span className="badge" style={{ background: "rgba(217, 255, 31, 0.15)", color: "var(--accent-lime)" }}>
                    {formatBytes(selectedFile?.size)}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="row g-3">
            {/* Sort Order */}
            <div className="col-md-6">
              <Form.Group controlId="uploadSortOrder">
                <Form.Label className="fw-semibold theme-form-label">Sort Order</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="1"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  disabled={uploading}
                  required
                  placeholder="e.g. 1"
                  style={{ borderRadius: 8, padding: "9px 14px" }}
                />
                <Form.Text className="small" style={{ color: "var(--text-secondary)" }}>
                  Ascending order on homepage (1 displays first).
                </Form.Text>
              </Form.Group>
            </div>

            {/* Status Switch */}
            <div className="col-md-6">
              <Form.Group controlId="uploadIsActive">
                <Form.Label className="fw-semibold theme-form-label d-block">Status</Form.Label>
                <div className="d-flex align-items-center gap-2 pt-2">
                  <div className="form-check form-switch mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="uploadActiveSwitch"
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                      disabled={uploading}
                      style={{ width: "2.6em", height: "1.3em", cursor: "pointer" }}
                    />
                  </div>
                  <label
                    htmlFor="uploadActiveSwitch"
                    className={`fw-semibold small mb-0 ${isActive ? "text-success" : "text-danger"}`}
                    style={{ cursor: "pointer" }}
                  >
                    {isActive ? "Active (Visible on homepage)" : "Inactive (Hidden)"}
                  </label>
                </div>
              </Form.Group>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer className="border-0 pt-0">
          <Button
            variant="secondary"
            onClick={() => {
              clearSelectedFile();
              onHide();
            }}
            disabled={uploading}
            style={{ borderRadius: 8, padding: "8px 20px" }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={uploading || !selectedFile}
            style={{ borderRadius: 8, padding: "8px 24px", minWidth: 120 }}
          >
            {uploading ? (
              <>
                <span className="admin-loader-sm me-2" role="status" aria-hidden="true"></span>
                Uploading...
              </>
            ) : (
              "Upload Slider"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default UploadSliderModal;
